import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Alert,
  Container,
  Paper,
  Stack,
  SvgIcon,
  InputLabel,
  Avatar
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCurrentUser, updateOrganizationTimezone } from '../userSlice';
import TimezoneSelect from 'react-timezone-select';
import onboardingService from '../services/onboardingService';
import { getOrganizationDetails } from '../services/organizationService';
import CountrySelect from './CountrySelect';
import { uploadOrganizationLogo, validateLogoFile } from '../services/logoService';

// Upload Icon from assets
const UploadIcon = () => (
  <SvgIcon width="20" height="20" viewBox="0 0 20 20">
    <path d="M10 1V13M10 1L15 6M10 1L5 6M19 13V17C19 17.5304 18.7893 18.0391 18.4142 18.4142C18.0391 18.7893 17.5304 19 17 19H3C2.46957 19 1.96086 18.7893 1.58579 18.4142C1.21071 18.0391 1 17.5304 1 17V13" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </SvgIcon>
);

// Time options for working hours
const TIME_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const hour = i.toString().padStart(2, '0');
  return {
    value: `${hour}:00`,
    label: `${hour}:00`,
  };
});

export default function OnboardingWelcomeStep() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.user);
  
  // Get user's current timezone for auto-detection
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  
  const [formData, setFormData] = useState({
    name: '',
    timeZone: userTimezone,
    defaultStartTime: '08:00',
    defaultEndTime: '18:00',
    companyAddress: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: ''
    }
  });
  
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoUploadLoading, setLogoUploadLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [onboardingData, orgData] = await Promise.all([
          onboardingService.getOnboardingStatus(),
          getOrganizationDetails()
        ]);
        
        setOnboardingStatus(onboardingData);
        
        // Pre-populate form with existing organization data, but keep auto-detected timezone if no org timezone exists
        if (orgData) {
          setFormData({
            name: orgData.name || '',
            // Only use org timezone if it exists AND is not the default UTC
            timeZone: (orgData.timeZone && orgData.timeZone !== 'UTC') ? orgData.timeZone : userTimezone,
            defaultStartTime: orgData.defaultStartTime || '08:00',
            defaultEndTime: orgData.defaultEndTime || '18:00',
            companyAddress: orgData.companyAddress || {
              line1: '',
              line2: '',
              city: '',
              state: '',
              postal_code: '',
              country: ''
            }
          });
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
        setError('Failed to load organization data');
      } finally {
        setInitialLoading(false);
      }
    };

    fetchData();
  }, [userTimezone]); // Add userTimezone as dependency

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested address fields
    if (name.startsWith('companyAddress.')) {
      const addressField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        companyAddress: {
          ...prev.companyAddress,
          [addressField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleTimezoneChange = (tz) => {
    setFormData(prev => ({
      ...prev,
      timeZone: tz.value
    }));
  };

  // Company ID removed per requirements

  // Validate logo file using service
  const validateLogoFileLocal = (file) => {
    try {
      validateLogoFile(file);
      return null; // No error
    } catch (error) {
      return error.message;
    }
  };

  // Handle logo upload
  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      console.log('Logo file selected:', file);
      
      const validationError = validateLogoFileLocal(file);
      if (validationError) {
        setError(validationError);
        return;
      }

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);
      setLogoFile(file);
      setError(''); // Clear any previous errors
    }
  };

  // Upload logo to server
  const uploadLogoToServer = async () => {
    if (!logoFile || !user?.currentOrganization?._id) return null;

    setLogoUploadLoading(true);
    try {
      const response = await uploadOrganizationLogo(logoFile);
      return response;
    } catch (error) {
      console.error('Error uploading logo:', error);
      setError(error.response?.data?.error || 'Failed to upload logo');
      return null;
    } finally {
      setLogoUploadLoading(false);
    }
  };

  // Clean up preview URL on unmount
  useEffect(() => {
    return () => {
      if (logoPreview) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setFieldErrors({});

    try {
      // Validate form - all fields required (including line2 per request)
      const errs = {};
      if (!formData.name.trim()) errs.name = 'Organization name is required';
      if (!formData.companyAddress.line1.trim()) errs.line1 = 'Street address is required';
      if (!formData.companyAddress.line2.trim()) errs.line2 = 'Address line 2 is required';
      if (!formData.companyAddress.city.trim()) errs.city = 'City is required';
      if (!formData.companyAddress.state.trim()) errs.state = 'State/Province is required';
      if (!formData.companyAddress.postal_code.trim()) errs.postal_code = 'Postal code is required';
      if (!formData.companyAddress.country) errs.country = 'Country is required';
      if (!formData.timeZone) errs.timeZone = 'Time zone is required';
      if (!formData.defaultStartTime) errs.defaultStartTime = 'Start time is required';
      if (!formData.defaultEndTime) errs.defaultEndTime = 'End time is required';
      if (!errs.defaultStartTime && !errs.defaultEndTime && formData.defaultStartTime >= formData.defaultEndTime) {
        errs.timeRange = 'End time must be after start time';
      }
      if (Object.keys(errs).length > 0) {
        setFieldErrors(errs);
        throw new Error('Please fix the highlighted fields');
      }

      // Update organization settings
      await onboardingService.updateOrganizationSettings(formData);
      
      // Upload logo if one was selected
      if (logoFile) {
        await uploadLogoToServer();
      }
      
      // Mark welcome step as completed
      await onboardingService.updateOnboardingStep('welcome', 'complete');
      
      // Update the Redux store with the new timezone
      dispatch(updateOrganizationTimezone(formData.timeZone));
      
      // Refresh user data to get updated onboarding status
      await dispatch(fetchCurrentUser());
      
      // Navigate to next step
      navigate('/onboarding/billing');
    } catch (error) {
      console.error('Error updating organization:', error);
      setError(error.response?.data?.error || error.message || 'Failed to update organization settings');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography>Loading organization data...</Typography>
        </Box>
      </Container>
    );
  }

  // Data for stepper - matching Figma design
  const steps = [
    { label: "Basic info", active: true },
    { label: "Billing", active: false },
    { label: "Complete", active: false },
  ];

  return (
    <Container maxWidth="md">
      <Stack spacing={10} alignItems="center">
        {/* Header Section */}
        <Stack spacing={6} alignItems="center" sx={{ width: "100%" }}>
          <Stack spacing={2} alignItems="center">
            <Typography
              variant="h3"
              sx={{
                fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                fontWeight: 500,
                color: 'text.primary',
                textAlign: 'center',
                lineHeight: '65px'
              }}
            >
              Let&apos;s create your organization!
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                fontWeight: 500,
                color: 'text.secondary',
                textAlign: 'center',
                maxWidth: '538px'
              }}
            >
              First, let&#39;s configure your organization&#39;s basic settings and working hours.
            </Typography>
          </Stack>

          {/* Progress Stepper */}
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            {steps.map((step, index) => (
              <Stack key={index} alignItems="center" spacing={1}>
                <Paper
                  elevation={0}
                  sx={{
                    width: 48,
                    height: 49,
                    borderRadius: 24,
                    backgroundColor: 'grey.300',
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: step.active
                      ? "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif"
                      : "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                    fontWeight: step.active ? 500 : 400,
                    color: step.active ? 'text.primary' : 'text.disabled',
                    textAlign: 'center'
                  }}
                  variant="h6"
                >
                  {step.label}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>

        {/* Form Section */}
        <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          <Stack spacing={6}>
            {/* Organization Name and Logo */}
            <Stack spacing={1}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <TextField
                  fullWidth
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your organization's name"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: 60,
                      borderColor: 'divider',
                      fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                    },
                  }}
                  error={!!fieldErrors.name}
                  helperText={fieldErrors.name}
                />

                <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: { xs: 2, sm: 0 } }}>
                  {/* Logo Preview */}
                  {logoPreview && (
                    <Avatar
                      src={logoPreview}
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: 2,
                        border: '2px solid',
                        borderColor: 'primary.main'
                      }}
                    />
                  )}
                  
                  <Button
                    component="label"
                    startIcon={<UploadIcon />}
                    disabled={logoUploadLoading}
                    sx={{
                      color: 'primary.main',
                      textTransform: "none",
                      fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                      fontSize: 16,
                      minWidth: 'auto',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {logoPreview ? 'Change logo' : 'Upload your logo'}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleLogoUpload}
                    />
                  </Button>
                </Stack>
              </Stack>
            </Stack>

            {/* Company Address */}
            <Stack spacing={3} sx={{ maxWidth: 743 }}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                  fontWeight: 500,
                  color: 'text.primary'
                }}
              >
                Company Address
              </Typography>

              <Stack spacing={2.5}>
                {/* Row 1: Street Address */}
                <TextField
                  fullWidth
                  name="companyAddress.line1"
                  value={formData.companyAddress.line1}
                  onChange={handleInputChange}
                  placeholder="Enter street address"
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: 60,
                      borderColor: 'divider',
                      fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                    },
                  }}
                  error={!!fieldErrors.line1}
                  helperText={fieldErrors.line1}
                />
                <TextField
                  fullWidth
                  name="companyAddress.line2"
                  value={formData.companyAddress.line2}
                  onChange={handleInputChange}
                  placeholder="Address line 2"
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: 60,
                      borderColor: 'divider',
                      fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                    },
                  }}
                  error={!!fieldErrors.line2}
                  helperText={fieldErrors.line2}
                />
                {/* Row 2: City, State, Postal Code */}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5}>
                  <TextField
                    name="companyAddress.city"
                    value={formData.companyAddress.city}
                    onChange={handleInputChange}
                    placeholder="City"
                    required
                    sx={{
                      flex: 1,
                      minWidth: 200,
                      "& .MuiOutlinedInput-root": {
                        height: 60,
                        borderColor: 'divider',
                        fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                      },
                    }}
                    error={!!fieldErrors.city}
                    helperText={fieldErrors.city}
                  />

                  <TextField
                    name="companyAddress.state"
                    value={formData.companyAddress.state}
                    onChange={handleInputChange}
                    placeholder="State/Province"
                    required
                    sx={{
                      flex: 1,
                      minWidth: 150,
                      "& .MuiOutlinedInput-root": {
                        height: 60,
                        borderColor: 'divider',
                        fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                      },
                    }}
                    error={!!fieldErrors.state}
                    helperText={fieldErrors.state}
                  />

                  <TextField
                    name="companyAddress.postal_code"
                    value={formData.companyAddress.postal_code}
                    onChange={handleInputChange}
                    placeholder="Postal Code"
                    required
                    sx={{
                      flex: 1,
                      minWidth: 120,
                      "& .MuiOutlinedInput-root": {
                        height: 60,
                        borderColor: 'divider',
                        fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                      },
                    }}
                    error={!!fieldErrors.postal_code}
                    helperText={fieldErrors.postal_code}
                  />
                </Stack>

                {/* Row 3: Country and Time zone */}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} alignItems={{ xs: 'stretch', md: 'center' }}>
                  <Box sx={{ flex: 1, minWidth: 250 }}>
                    <CountrySelect
                      value={formData.companyAddress.country}
                      onChange={(value) => handleInputChange({ target: { name: 'companyAddress.country', value } })}
                      required
                      error={!!fieldErrors.country}
                      helperText={fieldErrors.country}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: 60,
                          fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                        },
                      }}
                    />
                  </Box>

                  <Typography
                    sx={{
                      fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                      color: 'text.secondary',
                      fontSize: 16,
                      minWidth: 'auto',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Time zone:
                  </Typography>

                  <Box sx={{ flex: 1, minWidth: 250 }}>
                    {formData.timeZone ? (
                      <TimezoneSelect
                        key={formData.timeZone}
                        value={formData.timeZone}
                        onChange={handleTimezoneChange}
                        styles={{
                          control: (base) => ({
                            ...base,
                            height: 60,
                            minHeight: 60,
                            fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                            fontSize: 16,
                            borderColor: '#dbdbdb',
                            '&:hover': {
                              borderColor: '#dbdbdb'
                            }
                          }),
                          menuPortal: (base) => ({ 
                            ...base, 
                            zIndex: 1000 
                          }),
                          menu: (base) => ({ 
                            ...base, 
                            zIndex: 1000 
                          })
                        }}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      />
                    ) : (
                      <Typography>Loading timezone...</Typography>
                    )}
                    {fieldErrors.timeZone && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        {fieldErrors.timeZone}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Stack>
            </Stack>

            {/* Working Hours */}
            <Stack spacing={3} sx={{ maxWidth: 521 }}>
              <Stack spacing={1}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                    fontWeight: 500,
                    color: 'text.primary'
                  }}
                >
                  Working Hours:
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                    color: 'text.secondary',
                    fontSize: 16
                  }}
                >
                  These will be the default operating hours for your meeting rooms.
                  <br />
                  You can customize individual room hours later.
                </Typography>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Stack spacing={0.5}>
                  <Typography
                    sx={{
                      fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                      color: 'text.secondary',
                      fontSize: 13,
                      marginLeft: 2
                    }}
                  >
                    Start time
                  </Typography>
                  <FormControl sx={{ width: { xs: '100%', sm: 203 } }}>
                    <Select
                      name="defaultStartTime"
                      value={formData.defaultStartTime}
                      onChange={handleInputChange}
                      sx={{
                        height: 60,
                        borderColor: 'divider',
                        fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                      }}
                    >
                      {TIME_OPTIONS.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {fieldErrors.defaultStartTime && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                        {fieldErrors.defaultStartTime}
                      </Typography>
                    )}
                  </FormControl>
                </Stack>

                <Stack spacing={0.5}>
                  <Typography
                    sx={{
                      fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                      color: 'text.secondary',
                      fontSize: 13,
                      marginLeft: 2
                    }}
                  >
                    End time
                  </Typography>
                  <FormControl sx={{ width: { xs: '100%', sm: 203 } }}>
                    <Select
                      name="defaultEndTime"
                      value={formData.defaultEndTime}
                      onChange={handleInputChange}
                      sx={{
                        height: 60,
                        borderColor: 'divider',
                        fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                      }}
                    >
                      {TIME_OPTIONS.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {(fieldErrors.defaultEndTime || fieldErrors.timeRange) && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                        {fieldErrors.defaultEndTime || fieldErrors.timeRange}
                      </Typography>
                    )}
                  </FormControl>
                </Stack>
              </Stack>
            </Stack>

            {/* Continue Button */}
            <Button
              type="submit"
              variant="contained"
              disabled={loading || logoUploadLoading}
              sx={{
                width: { xs: '100%', sm: 403 },
                height: 60,
                backgroundColor: 'primary.main',
                borderRadius: 1,
                textTransform: "none",
                boxShadow: "0px 13px 9.7px -8px rgba(32, 141, 244, 0.24)",
                fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                fontWeight: 600,
                fontSize: 16,
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                '&:disabled': {
                  backgroundColor: 'action.disabledBackground',
                  color: 'action.disabled'
                }
              }}
            >
              {loading ? 'Saving...' : logoUploadLoading ? 'Uploading logo...' : 'Continue to billing set up'}
            </Button>
          </Stack>
        </Box>
      </Stack>
    </Container>
  );
} 